import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, LocateFixed, X } from "lucide-react";

const pin = L.divIcon({
  className: "",
  html: `<div style="background:#e11d48;width:20px;height:20px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 1px 6px rgba(0,0,0,.5)"></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 20],
});

export function LocationPicker({ value, center = [5.6037, -0.187], onChange, height = 220 }) {
  const el = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);
  const justPicked = useRef(false);
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const place = (lat, lng, pan = true) => {
    if (!map.current) return;
    if (!marker.current) {
      marker.current = L.marker([lat, lng], { icon: pin, draggable: true }).addTo(map.current);
      marker.current.on("dragend", () => {
        const p = marker.current.getLatLng();
        onChange?.(p.lat, p.lng);
      });
    } else {
      marker.current.setLatLng([lat, lng]);
    }
    if (pan) map.current.setView([lat, lng], Math.max(map.current.getZoom(), 15));
  };

  useEffect(() => {
    if (!el.current || map.current) return;
    const c = value?.lat != null ? [value.lat, value.lng] : center;
    map.current = L.map(el.current, { attributionControl: false }).setView(c, value?.lat != null ? 15 : 13);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(map.current);
    map.current.on("click", (e) => { place(e.latlng.lat, e.latlng.lng, false); onChange?.(e.latlng.lat, e.latlng.lng); });
    if (value?.lat != null) place(value.lat, value.lng, false);
    setTimeout(() => map.current && map.current.invalidateSize(), 150);
    return () => { map.current?.remove(); map.current = null; marker.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (value?.lat == null && marker.current) { marker.current.remove(); marker.current = null; }
    else if (value?.lat != null && !marker.current) place(value.lat, value.lng, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.lat, value?.lng]);

  const search = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=gh&q=${encodeURIComponent(query)}`);
      const arr = await r.json();
      if (arr[0]) { const lat = parseFloat(arr[0].lat), lng = parseFloat(arr[0].lon); place(lat, lng); onChange?.(lat, lng); setSuggestions([]); }
    } catch { /* ignore */ }
    finally { setSearching(false); }
  };

  const pick = (item) => {
    justPicked.current = true;
    const lat = parseFloat(item.lat), lng = parseFloat(item.lon);
    setQuery(item.display_name);
    setSuggestions([]);
    place(lat, lng);
    onChange?.(lat, lng);
  };

  useEffect(() => {
    if (justPicked.current) { justPicked.current = false; return; }
    if (query.trim().length < 3) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=6&countrycodes=gh&q=${encodeURIComponent(query)}`);
        setSuggestions(await r.json());
      } catch { setSuggestions([]); }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);
  const useCurrent = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => { place(p.coords.latitude, p.coords.longitude); onChange?.(p.coords.latitude, p.coords.longitude); },
      () => {}, { enableHighAccuracy: true, timeout: 8000 });
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Input data-testid="pos-map-search" value={query} onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (suggestions[0]) pick(suggestions[0]); else search(); } }}
            placeholder="Search a place to pin…" className="h-9" autoComplete="off" />
          {suggestions.length > 0 && (
            <div className="absolute left-0 right-0 top-10 z-[1000] bg-popover border border-border rounded-md shadow-lg max-h-56 overflow-y-auto" data-testid="pos-map-suggestions">
              {suggestions.map((s, i) => (
                <button type="button" key={`${s.place_id || i}`} data-testid={`pos-map-suggestion-${i}`}
                  className="block w-full text-left px-3 py-2 text-xs hover:bg-muted border-b border-border last:border-0"
                  onClick={() => pick(s)}>{s.display_name}</button>
              ))}
            </div>
          )}
        </div>
        <Button type="button" variant="outline" size="sm" className="h-9" data-testid="pos-map-search-btn" onClick={search} disabled={searching}><Search className="h-4 w-4" /></Button>
        <Button type="button" variant="outline" size="sm" className="h-9" data-testid="pos-map-locate-btn" onClick={useCurrent} title="Use current location"><LocateFixed className="h-4 w-4" /></Button>
      </div>
      <div ref={el} data-testid="pos-location-picker" style={{ height, width: "100%", borderRadius: 10, zIndex: 0 }} />
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span data-testid="pos-map-coords">{value?.lat != null ? `📍 Pinned: ${Number(value.lat).toFixed(5)}, ${Number(value.lng).toFixed(5)}` : "Tap the map or search to drop a pin (optional)"}</span>
        {value?.lat != null && <button type="button" data-testid="pos-map-clear" className="inline-flex items-center gap-1 text-rose-600" onClick={() => onChange?.(null, null)}><X className="h-3 w-3" /> Clear pin</button>}
      </div>
    </div>
  );
}
